import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Inject, Component, OnInit } from "@angular/core";
import { ChangeOrderArea, ChangeOrderAreaSummary } from "../models/change.order.area.summary.interface";
import { NgForm } from "@angular/forms";
import { JobService } from "../services/job.service";
import { finalize } from "rxjs/operators";
import { PaymentTerm } from "../../shared/models/payment.term.interface";
import { PaymentType } from "../../shared/models/payment.type.interface";
import { DeviceDetectorService } from "ngx-device-detector";
import { ChangeOrder } from "../../shared/models/change.order.interface";
import { ChangeOrderItem } from "../../shared/models/change.order.item.interface";
import { AbstractBiditComponent } from "../../shared/controls/abstract.bidit.component";
import { DealerService } from "../../dealer/services/dealer.service";
import { CalculateService } from "../../shared/services/calculate.service";
import { RolePermission } from "../../shared/models/role.permission.enum";

export interface ConvertDialogData {
  changeOrderId: number;
  dealerRounding: number;
}

@Component({
  selector: 'change.order.approve.dialog',
  templateUrl: 'change.order.approve.dialog.html',
  styleUrls: ['./change.order.approve.dialog.scss']
})
export class ChangeOrderApproveDialog extends AbstractBiditComponent implements OnInit {
  
  paymentTypes: PaymentType[];
  paymentTerms: PaymentTerm[];

  area_IsAccepted305: any;

  upgradeAreaSummaries: ChangeOrderAreaSummary[] = new Array();

  changeOrder: ChangeOrder = {} as ChangeOrder;

  showMarginEdit: boolean;
  showBidAmountEdit: boolean;

  changeOrderTotalBidAmount: number;
  totalLaborCost: number;
  totalMaterialCost: number;
  totalOtherCost: number;

  materialTaxTotal: number;
  laborTaxTotal: number;
  otherTaxTotal: number;
  netTaxTotal: number;
  dealerRoundingType: number;
  showMargins: boolean = false;
  show

  constructor(
    private jobService: JobService,
    private dealerService: DealerService,
    private deviceDetectorService: DeviceDetectorService,
    private calculateService: CalculateService,
    public dialogRef: MatDialogRef<ChangeOrderApproveDialog>,
    @Inject(MAT_DIALOG_DATA) public data: ConvertDialogData) {
    super(deviceDetectorService);
  }

  ngOnInit() {
    this.jobService.getChangeOrderSummary(this.data.changeOrderId)
      .subscribe(result => {
        this.changeOrder = result.changeOrder;

        this.upgradeAreaSummaries = this.calculateUpgradeAreas(result.areaSummaries);
        this.netTaxTotal = result.changeOrder.netTax_Total;
        this.changeOrderTotalBidAmount = result.changeOrder.changeOrder_BidAmount + this.netTaxTotal;
        this.getDealerRoundingType();
      });
    this.showMargins = JobService.hasPermission(RolePermission.ViewEditMargins);

  }

  getDealerRoundingType(): void {
    this.dealerService.getDealerDetails ()
      .subscribe(result => {
        this.dealerRoundingType = result.dealer_Rounding;
      });
  }

  calculateUpgradeAreas(areaSummaries: ChangeOrderAreaSummary[]): ChangeOrderAreaSummary[] {    
    areaSummaries.forEach(a => {
      a.quoteAreas.forEach(qa => {
        if (qa.changeOrderItem == null) return;
        qa.changeOrderItem.changeOrderItem_RadioValue = 1;
      });      

      a.newItems.forEach(ni => {
        ni.changeOrderItem_RadioValue = 1;
      });
    });

    return areaSummaries;
  }

  editBid() {
    this.showBidAmountEdit = true;
  }

  editMargin() {
    this.showMarginEdit = true;
  }

  calculateProjectCost() {
    this.changeOrder.totalCost = 0;
    this.totalMaterialCost = 0;
    this.totalLaborCost = 0;
    this.totalOtherCost = 0;

    this.upgradeAreaSummaries.forEach(a => {
      a.quoteAreas.forEach(qa => {
        if (qa.changeOrderItem == null || !qa.changeOrderItem.changeOrderItem_RadioValue) return;

        this.changeOrder.totalCost += (qa.area.area_IsOption ?
          Math.round(qa.changeOrderItem.changeOrderItem_TotalCost / ((100 - qa.changeOrderItem.changeOrderItem_GrossMargin) / 100)) :
          qa.changeOrderItem.changeOrderItem_TotalCost);

        this.totalLaborCost += qa.changeOrderItem.changeOrderItem_TotalLaborCost ?? 0;
        this.totalMaterialCost += qa.changeOrderItem.changeOrderItem_MaterialCost ?? 0;
        this.totalOtherCost += qa.changeOrderItem.changeOrderItem_TotalOther ?? 0;
      });

      a.newItems.forEach(coi => {
        if (!coi.changeOrderItem_RadioValue) return;

        this.changeOrder.totalCost += (coi.area.area_IsOption ?
          Math.round(coi.changeOrderItem_TotalCost / ((100 - coi.changeOrderItem_GrossMargin) / 100)) :
          coi.changeOrderItem_TotalCost);

        this.totalLaborCost += coi.changeOrderItem_TotalLaborCost ?? 0;
        this.totalMaterialCost += coi.changeOrderItem_MaterialCost ?? 0;
        this.totalOtherCost += coi.changeOrderItem_TotalOther ?? 0;
      });
    });

    this.calcTaxTotals();
  }

  calculateConversion() {
    this.calculateProjectCost();
    let marginRatio = (100 - (this.changeOrder.changeOrder_GrossMargin ?? 0)) / 100;

    this.changeOrder.changeOrder_BidAmount = this.calculateService.GetRoundedAmount(this.dealerRoundingType, (this.changeOrder.totalCost / marginRatio));
    this.changeOrderTotalBidAmount = this.changeOrder.changeOrder_BidAmount + this.netTaxTotal;
  }

  calcTaxTotals() {
    this.changeOrder.materialTax_Rate ?
      (this.materialTaxTotal = (this.changeOrder.materialTax_Rate * this.totalMaterialCost) / 100) :
      this.materialTaxTotal = this.changeOrder.materialTax_Total;

    this.changeOrder.laborTax_Rate ?
      (this.laborTaxTotal = (this.changeOrder.laborTax_Rate * this.totalLaborCost) / 100) :
      this.laborTaxTotal = this.changeOrder.laborTax_Total;

    this.changeOrder.otherTax_Rate ?
      (this.otherTaxTotal = (this.changeOrder.otherTax_Rate * this.totalOtherCost) / 100) :
      this.otherTaxTotal = this.changeOrder.otherTax_Total;

    this.netTaxTotal = this.materialTaxTotal + this.laborTaxTotal + this.otherTaxTotal;
  }

  updateMargin() {
    this.showMarginEdit = false;

    this.changeOrder.changeOrder_GrossMargin = Math.round(this.changeOrder.changeOrder_GrossMargin * 10000) / 10000;
    this.calculateConversion();
  }

  updateBid() {
    this.showBidAmountEdit = false;

    this.calculateProjectCost();

    this.changeOrder.changeOrder_BidAmount = this.calculateService.GetRoundedAmount(this.dealerRoundingType, this.changeOrder.changeOrder_BidAmount);

    let marginRation = (this.changeOrder.totalCost / this.changeOrder.changeOrder_BidAmount);
    this.changeOrder.changeOrder_GrossMargin = (100 - (100 * marginRation));

    this.changeOrder.changeOrder_GrossMargin = Math.round(this.changeOrder.changeOrder_GrossMargin * 10000) / 10000;
  }

  approveChangeOrder(form: NgForm) {
    this.errors = '';

    if (form.valid) {
      let quoteAreas = 0;
      let acceptedAreas: number[] = [];
      this.upgradeAreaSummaries.forEach(a => {
        var selections = a.quoteAreas.map((qa: ChangeOrderArea): number => {
          quoteAreas++;
          if (qa.changeOrderItem == null || qa.changeOrderItem.changeOrderItem_RadioValue == 0) return 0;
          if (qa.changeOrderItem.changeOrderItem_IsDelete) quoteAreas--;

          return qa.changeOrderItem.changeOrderItem_ID;
        });
        acceptedAreas = acceptedAreas.concat(selections);

        var selections = a.newItems.map((coi: ChangeOrderItem): number => {
          if (coi.changeOrderItem_RadioValue == 0) return 0;

          quoteAreas++;
          return coi.changeOrderItem_ID;
        });
        acceptedAreas = acceptedAreas.concat(selections);
      });

      let accepted = acceptedAreas.find(ae => ae != 0);
      if (accepted == null) {
        alert($localize`No changes accepted`);
        return;
      }

      if (quoteAreas == 0) {
        alert($localize`Cannot approve.  Change would leave job with no remaining products.`);
        return;
      }

      this.jobService.approveChangeOrder(this.changeOrder.changeOrder_ID,
        this.changeOrder.changeOrder_BidAmount,
        acceptedAreas)
        .pipe(finalize(() => this.isRequesting = false))
        .subscribe({
          next: (result) => {
            if (result) {
              this.dialogRef.close(this.data);
            }
          },
          error: (error: string) => this.errors = error
        });
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

}
